import { authFetch, downloadFile } from '@/composables/utils'
import type { ApiResponse, Course, CourseFilters, PaginatedCourseResponse } from '@/types/course'
import type { Sex, Trainer } from '@/types/trainer.ts'
import type { Participant } from '@/types/participant.ts'

const UPLOAD_API_ENDPOINT = '/api/courses/upload/'
const SUBMIT_API_ENDPOINT = '/api/courses/submit/'
const INSERT_API_ENDPOINT = '/api/courses/insert/'
const DOWNLOAD_TEMPLATE_API_ENDPOINT = '/api/courses/download_template/'

export type CourseContext = 'trainers' | 'participants'

/**
 * Retrieves a paginated list of courses with optional filters.
 */
export async function getCourses(
  context: CourseContext,
  page: number = 1,
  filters: CourseFilters = {},
  pageSize: number = 30,
): Promise<PaginatedCourseResponse> {
  try {
    const COURSE_API_ENDPOINT = `/api/courses/${context}/`
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    })

    if (filters.course_type_id && filters.course_type_id.length > 0)
      params.append('course_type', filters.course_type_id.join(','))
    if (filters.organization_id && filters.organization_id.length > 0)
      params.append('organization', filters.organization_id.join(','))
    if (filters.world_area_id && filters.world_area_id.length > 0)
      params.append('world_area', filters.world_area_id.join(','))
    if (filters.country_id && filters.country_id.length > 0)
      params.append('country_id', filters.country_id.join(','))
    if (filters.start_date) params.append('start_date', filters.start_date)
    if (filters.end_date) params.append('end_date', filters.end_date)

    const response = await authFetch(`${COURSE_API_ENDPOINT}?${params.toString()}`, {
      cache: 'no-store',
    })
    return response as PaginatedCourseResponse
  } catch (error) {
    console.error('Error fetching courses:', error)
    throw error
  }
}

/**
 * Retrieves a single course by its ID for a given context.
 * @param {CourseContext} context - The context ('trainers' or 'participants').
 * @param {number} courseId - The ID of the course to retrieve.
 * @param {boolean} forceRefresh - If true, appends a timestamp to bypass cache.
 * @returns {Promise<Course>} The course data.
 */
export async function getCourseById(
  context: CourseContext,
  courseId: number,
  forceRefresh = false,
): Promise<Course> {
  try {
    const COURSE_API_ENDPOINT = `/api/course/`
    let url = `${COURSE_API_ENDPOINT}${courseId}/`
    if (forceRefresh) {
      url += `?_t=${new Date().getTime()}`
    }
    const response = await authFetch(url, { cache: 'no-store' })
    return response as Course
  } catch (error) {
    console.error(`Error fetching course with ID ${courseId}:`, error)
    throw error
  }
}

/**
 * Retrieves all trainers for a specific course.
 * @param {number} courseId - The ID of the course.
 * @param {boolean} forceRefresh - If true, appends a timestamp to bypass cache.
 * @returns {Promise<Trainer[]>} A list of trainers.
 */
export async function getCourseTrainers(
  courseId: number,
  forceRefresh = false,
): Promise<Trainer[]> {
  try {
    let url = `/api/course/${courseId}/trainers/`
    if (forceRefresh) {
      url += `?_t=${new Date().getTime()}`
    }
    const response = await authFetch(url, { cache: 'no-store' })
    return response as Trainer[]
  } catch (error) {
    console.error(`Error fetching trainers for course with ID ${courseId}:`, error)
    throw error
  }
}

/**
 * Retrieves all participants for a specific course.
 * @param {number} courseId - The ID of the course.
 * @param {boolean} forceRefresh - If true, appends a timestamp to bypass cache.
 * @returns {Promise<Participant[]>} A list of participants.
 */
export async function getCourseParticipants(
  courseId: number,
  forceRefresh = false,
): Promise<Participant<Sex>[]> {
  try {
    let url = `/api/course/${courseId}/participants/`
    if (forceRefresh) {
      url += `?_t=${new Date().getTime()}`
    }
    const response = await authFetch(url, { cache: 'no-store' })
    return response as Participant<Sex>[]
  } catch (error) {
    console.error(`Error fetching participants for course with ID ${courseId}:`, error)
    throw error
  }
}

/**
 * Carica un file excel del corso.
 * @param {File} file - Il file da caricare.
 * @returns {Promise<ApiResponse>} La risposta JSON dal backend.
 */
export async function uploadCourseFile(file: File): Promise<ApiResponse> {
  const formData = new FormData()
  formData.append('file', file)

  try {
    const response = await authFetch(UPLOAD_API_ENDPOINT, {
      method: 'POST',
      body: formData,
    })
    return response as ApiResponse
  } catch (error) {
    console.error('Error uploading course file:', error)
    throw error
  }
}

/**
 * Invia i dati del corso modificati per la finalizzazione.
 * @param {ApiResponse} data - I dati del corso modificati.
 * @returns {Promise<any>} La risposta di successo/fallimento dal backend.
 */
export async function submitCourseData(data: ApiResponse): Promise<unknown> {
  try {
    return await authFetch(SUBMIT_API_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  } catch (error) {
    console.error('Error submitting course data:', error)
    throw error
  }
}

/**
 * Inserts the simplified course data.
 * @param {object} data - The simplified course data.
 * @returns {Promise<any>} The response from the backend.
 */
export async function insertCourseData(data: object): Promise<unknown> {
  console.log('data: \n', JSON.stringify(data))
  try {
    return await authFetch(INSERT_API_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  } catch (error) {
    console.error('Error inserting course data:', error)
    throw error
  }
}

/**
 * Downloads the course template file.
 */
export async function downloadCourseTemplate(): Promise<void> {
  try {
    const response = (await authFetch(DOWNLOAD_TEMPLATE_API_ENDPOINT, {}, true)) as Response
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'course_template.xlsx'
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading course template:', error)
    throw error
  }
}

/**
 * Downloads a single attendance certificate PDF for one participant.
 * @param {number} courseId - The ID of the course.
 * @param {number} profileId - The ID of the participant profile.
 */
export async function downloadAttendanceCertificate(
  courseId: number,
  profileId: number,
): Promise<void> {
  try {
    const response = (await authFetch(
      `/api/course/${courseId}/download_attendance_certificate/${profileId}/`,
      { method: 'GET' },
      true,
    )) as Response

    if (!response.ok) {
      throw new Error('Download failed')
    }

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const contentDisposition = response.headers.get('content-disposition')
    let filename = `certificate_${profileId}.pdf`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      if (match?.[1]) {
        filename = match[1]
      }
    }
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading attendance certificate:', error)
    throw error
  }
}

/**
 * Downloads attendance certificates as a ZIP file.
 * @param {number} courseId - The ID of the course.
 */
export async function downloadAttendanceCertificates(courseId: number): Promise<void> {
  try {
    const response = (await authFetch(
      `/api/course/${courseId}/download_attendance_zip/`,
      { method: 'GET' },
      true,
    )) as Response

    if (!response.ok) {
      throw new Error('Download failed')
    }

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const contentDisposition = response.headers.get('content-disposition')
    let filename = `certificates_${courseId}.zip`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      if (match && match[1]) {
        filename = match[1]
      }
    }
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading attendance certificates:', error)
    throw error
  }
}

/**
 * Deletes multiple courses by their IDs.
 * @param context
 * @param {number[]} ids - An array of course IDs to delete.
 * @returns {Promise<void>}
 */
export async function deleteCourses(context: CourseContext, ids: number[]): Promise<void> {
  try {
    const COURSE_API_ENDPOINT = `/api/courses/${context}/`
    await authFetch(`${COURSE_API_ENDPOINT}bulk_delete/`, {
      method: 'POST',
      body: JSON.stringify({ ids }),
    })
  } catch (error) {
    console.error('Error deleting courses:', error)
    throw error
  }
}

/**
 * Updates course details.
 * @param {number} courseId - The ID of the course to update.
 * @param {object} payload - The update payload.
 * @returns {Promise<Course>} The updated course data.
 */
export async function patchCourse(courseId: number, payload: unknown): Promise<Course> {
  try {
    const response = await authFetch('/api/course/' + courseId + '/', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
    return response as Course
  } catch (error) {
    console.error(`Error patching course with ID ${courseId}:`, error)
    throw error
  }
}

/**
 * Downloads ETB ToT Phase 2 certificates as a ZIP file for all trained trainers.
 * @param {number} courseId - The ID of the course.
 */
export async function downloadEtbPh2Certificates(courseId: number): Promise<void> {
  try {
    const response = (await authFetch(
      `/api/course/${courseId}/download_etb_ph2_zip/`,
      { method: 'GET' },
      true,
    )) as Response

    if (!response.ok) {
      throw new Error('Download failed')
    }

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const contentDisposition = response.headers.get('content-disposition')
    let filename = `etb_ph2_certificates_${courseId}.zip`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      if (match && match[1]) {
        filename = match[1]
      }
    }
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading ETB Ph2 certificates:', error)
    throw error
  }
}

/**
 * Downloads a single ETB ToT Phase 2 certificate PDF for one trained trainer.
 * @param {number} courseId - The ID of the course.
 * @param {number} profileId - The ID of the trained trainer profile.
 */
export async function downloadEtbPh2Certificate(
  courseId: number,
  profileId: number,
): Promise<void> {
  try {
    const response = (await authFetch(
      `/api/course/${courseId}/download_etb_ph2_certificate/${profileId}/`,
      { method: 'GET' },
      true,
    )) as Response

    if (!response.ok) {
      throw new Error('Download failed')
    }

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const contentDisposition = response.headers.get('content-disposition')
    let filename = `etb_ph2_certificate_${profileId}.pdf`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      if (match?.[1]) {
        filename = match[1]
      }
    }
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading ETB Ph2 certificate:', error)
    throw error
  }
}

/**
 * Downloads courses data as an XLSX file.
 * @param {CourseContext} context - The context ('trainers' or 'participants').
 * @param {CourseFilters} filters - The optional filters to apply.
 */
export async function downloadCourses(
  context: CourseContext,
  filters: CourseFilters = {},
): Promise<void> {
  const COURSE_API_ENDPOINT = `/api/courses/${context}/`
  const params = new URLSearchParams({
    all: 'true',
    format: 'xlsx',
  })

  if (filters.course_type_id?.length) params.append('course_type', filters.course_type_id.join(','))
  if (filters.organization_id?.length)
    params.append('organization', filters.organization_id.join(','))
  if (filters.world_area_id?.length) params.append('world_area', filters.world_area_id.join(','))
  if (filters.country_id?.length) params.append('country_id', filters.country_id.join(','))
  if (filters.start_date) params.append('start_date', filters.start_date)
  if (filters.end_date) params.append('end_date', filters.end_date)

  await downloadFile(COURSE_API_ENDPOINT, params)
}

/**
 * Deletes a single course by its ID.
 *
 * Pass `force: true` with a mandatory `reason` (≥5 chars) to bypass the
 * issued-certificate guard. Requires the `course.force_delete_course`
 * permission (Super Administrator only). All related data is soft-deleted
 * and remains recoverable.
 *
 * @param {number} courseId - The ID of the course to delete.
 * @param {boolean} force - When true, bypasses the issued-certificate guard.
 * @param {string} reason - Mandatory audit reason when force is true.
 * @returns {Promise<void>}
 */
export async function deleteCourse(courseId: number, force = false, reason = ''): Promise<void> {
  try {
    const body: Record<string, unknown> = {}
    if (force) {
      body.force = true
      body.reason = reason
    }
    await authFetch(`/api/course/${courseId}/`, {
      method: 'DELETE',
      body: Object.keys(body).length ? JSON.stringify(body) : undefined,
    })
  } catch (error) {
    console.error(`Error deleting course with ID ${courseId}:`, error)
    throw error
  }
}

/**
 * Merges multiple courses into one.
 * @param {number} entityId - The ID of the course to keep.
 * @param {number[]} sourceIds - An array of IDs of the courses to merge and delete.
 * @returns {Promise<any>} The response from the backend.
 */
export async function mergeCourses(entityId: number, sourceIds: number[]): Promise<unknown> {
  try {
    const response = await authFetch(`/api/course/${entityId}/merge/`, {
      method: 'POST',
      body: JSON.stringify({
        source_ids: sourceIds,
        body: {},
      }),
    })
    return response
  } catch (error) {
    console.error('Error merging courses:', error)
    throw error
  }
}

/**
 * Adds entities (Trainers or Participants) to a course.
 * @param {number} courseId - The ID of the course.
 * @param {string} type - 'T' for Trainer, 'P' for Participant.
 * @param {addEntitiesToCoursePayload[]} payload - The payload containing values or data.
 */
export async function addEntitiesToCourse(
  courseId: number,
  type: 'T' | 'P',
  payload: addEntitiesToCoursePayload[],
): Promise<unknown> {
  return authFetch(`/api/course/${courseId}/add/${type}/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

/**
 * Removes entities (Trainers or Participants) from a course.
 * @param {number} courseId - The ID of the course.
 * @param {string} type - 'T' for Trainer, 'P' for Participant.
 * @param {removeEntitiesFromCoursePayload[]} payload - The payload containing IDs to remove & force parameter.
 */
export async function removeEntitiesFromCourse(
  courseId: number,
  type: 'T' | 'P',
  payload: removeEntitiesFromCoursePayload[],
): Promise<unknown> {
  return authFetch(`/api/course/${courseId}/delete/${type}/`, {
    method: 'DELETE',
    body: JSON.stringify(payload),
  })
}

export interface removeEntitiesFromCoursePayload {
  value: number
  force?: boolean
  // reason?: string
}

/**
 * Adds an organization to a course.
 * @param {number} courseId - The ID of the course.
 * @param {number} orgId - The ID of the organization to add.
 */
export async function addOrganizationToCourse(courseId: number, orgId: number): Promise<unknown> {
  return authFetch(`/api/course/${courseId}/add_organization/`, {
    method: 'POST',
    body: JSON.stringify({ id: orgId }),
  })
}

/**
 * Removes an organization from a course.
 * @param {number} courseId - The ID of the course.
 * @param {number} orgId - The ID of the organization to remove.
 */
export async function removeOrganizationFromCourse(courseId: number, orgId: number): Promise<void> {
  await authFetch(`/api/course/${courseId}/remove_organization/`, {
    method: 'DELETE',
    body: JSON.stringify({ id: orgId }),
  })
}

// FIX: Updated interface to allow data
export interface addEntitiesToCoursePayload {
  value?: number
  data?: unknown // Changed from 'never' to 'unknown' to allow object payload
}
